import { supabase } from "@/integrations/supabase/client";
import type {
  ATSScore,
  ResumeAnalysis,
  ResumeData,
  ExperienceItem,
  EducationItem,
  CustomSection,
  SkillCategory,
} from "@/types/resume";
import { normalizeDateString, normalizeWorkType, parseDateRange } from "@/lib/dateParsing";

// ── Config ──
// Edge function timeouts: stream=70s, nonStream=58s (gpt-4o-mini with tool use can take 35-60s).
// Client timeouts add ~20s buffer so the edge function can finish and send an error event
// before the client aborts the connection.
const STREAM_TIMEOUT_MS = 92_000;
const TAILOR_STREAM_TIMEOUT_MS = 92_000;
const NON_STREAM_TIMEOUT_MS = 80_000;
const TAILOR_NON_STREAM_TIMEOUT_MS = 80_000;
const MAX_RETRIES = 1;
const RETRY_DELAY_MS = 500;

// ── Cache ──
const optimizationCache = new Map<string, { result: any; timestamp: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000;

function generateCacheKey(data: any): string {
  const str = JSON.stringify(data);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return `opt_${hash}`;
}

function getCached<T>(key: string): T | null {
  const entry = optimizationCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) { optimizationCache.delete(key); return null; }
  return entry.result as T;
}

function setCache(key: string, result: any) {
  if (optimizationCache.size > 20) {
    const oldest = optimizationCache.keys().next().value;
    if (oldest) optimizationCache.delete(oldest);
  }
  optimizationCache.set(key, { result, timestamp: Date.now() });
}

function toStringArray(value: any): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v) => typeof v === "string").map((v) => v.trim()).filter(Boolean);
}

// ── Auth header ──
// Always use the session access token (signed by the correct Supabase project).
// The anon key in VITE_SUPABASE_PUBLISHABLE_KEY may belong to the dev project and
// would fail JWT verification against the production project. The session token is
// kept fresh by autoRefreshToken: true in the supabase client.
async function getAuthHeaders(): Promise<{ Authorization: string; apikey: string }> {
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) {
      return { Authorization: `Bearer ${token}`, apikey: key };
    }
  } catch {
    // fall through
  }
  return { Authorization: `Bearer ${key}`, apikey: key };
}

// ── Progress callback with anti-regression ──
export type ProgressCallback = (stage: string, percent?: number) => void;

/** Wraps a progress callback to ensure percent never goes backwards */
function createMonotonicProgress(onProgress?: ProgressCallback): ProgressCallback | undefined {
  if (!onProgress) return undefined;
  let maxPercent = 0;
  return (stage: string, percent?: number) => {
    if (percent !== undefined) {
      maxPercent = Math.max(maxPercent, percent);
      onProgress(stage, maxPercent);
    } else {
      onProgress(stage);
    }
  };
}

// ── SSE Stream consumer ──
async function invokeStreamingFunction<T>(
  body: any,
  onProgress?: ProgressCallback,
  timeoutMs = STREAM_TIMEOUT_MS,
): Promise<T> {
  // Ensure no trailing slash on URL
  const baseUrl = (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "");
  const url = `${baseUrl}/functions/v1/optimize-resume`;
  const authHeaders = await getAuthHeaders();

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({ ...body, stream: true }),
      signal: controller.signal,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      let parsed: any = null;
      try { parsed = JSON.parse(text); } catch {}
      throw new Error(parsed?.error || `Request failed (${response.status})`);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let result: T | null = null;
    let streamError: string | null = null;
    // Pending event type for the two-line SSE format (event: TYPE \n data: JSON)
    let pendingEventType: string | null = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let newlineIdx: number;
      while ((newlineIdx = buffer.indexOf("\n")) !== -1) {
        let line = buffer.slice(0, newlineIdx);
        buffer = buffer.slice(newlineIdx + 1);
        if (line.endsWith("\r")) line = line.slice(0, -1);

        // Blank line = SSE event boundary; reset pending state
        if (line === "") {
          pendingEventType = null;
          continue;
        }

        if (line.startsWith("event: ")) {
          pendingEventType = line.slice(7).trim();
          continue;
        }

        if (line.startsWith("data: ")) {
          const jsonStr = line.slice(6);
          // Skip SSE keep-alive or [DONE] markers
          if (jsonStr === "[DONE]" || jsonStr === ": keep-alive") continue;
          try {
            const data = JSON.parse(jsonStr);
            const eventType = pendingEventType || "message";

            if (eventType === "progress") {
              onProgress?.(data.stage, data.percent);
            } else if (eventType === "result") {
              result = data as T;
            } else if (eventType === "error") {
              streamError = data.message || data.error || "Processing failed";
            } else if (eventType === "done") {
              // Stream complete — nothing to do
            } else {
              // Unknown event or plain data: — try to interpret as result if it looks like one
              if (data && typeof data === "object" && (data.parsedResumeData || data.optimizedSummary)) {
                result = data as T;
              }
            }
          } catch {
            // Malformed JSON in SSE line — skip silently
          }
          pendingEventType = null;
        }
      }
    }

    if (streamError) throw new Error(streamError);
    if (!result) throw new Error("No result received. Please try again.");
    return result;
  } catch (error: any) {
    if (error?.name === "AbortError") {
      throw new Error("Processing is taking longer than expected. Please try again.");
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

// ── Non-streaming invoke with retry ──
function isRetryableError(error: any): boolean {
  const message = error?.message?.toLowerCase() || "";
  return message.includes("timeout") || message.includes("rate limit") ||
    message.includes("503") || message.includes("502") || message.includes("500") ||
    message.includes("network") || message.includes("temporarily unavailable") ||
    message.includes("timed out") || message.includes("abort");
}

async function invokeFunctionWithRetry<T>(
  functionName: string,
  options: any,
  timeoutMs = NON_STREAM_TIMEOUT_MS,
  maxRetries = MAX_RETRIES,
  onProgress?: ProgressCallback
): Promise<{ data: T | null; error: any }> {
  // Use direct fetch with the session JWT (via getAuthHeaders) so the token is
  // always signed by the correct Supabase project. The anon key in
  // VITE_SUPABASE_PUBLISHABLE_KEY may belong to the dev project and fails JWT
  // verification against the production project.
  const baseUrl = (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "");
  const url = `${baseUrl}/functions/v1/${functionName}`;
  const body = options.body;

  let lastError: any = null;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      onProgress?.("Retrying...");
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * attempt));
    }
    const authHeaders = await getAuthHeaders();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
          ...(options.headers || {}),
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        let parsed: any = null;
        try { parsed = JSON.parse(text); } catch {}
        const errMsg = parsed?.error || `Request failed (${response.status})`;
        lastError = new Error(errMsg);
        if (isRetryableError(lastError) && attempt < maxRetries) continue;
        return { data: null, error: lastError };
      }
      const data = await response.json() as T;
      return { data, error: null };
    } catch (error: any) {
      if (error?.name === "AbortError") {
        lastError = new Error("Request timed out");
        if (attempt < maxRetries) { continue; }
        return { data: null, error: lastError };
      }
      lastError = error;
      if (isRetryableError(error) && attempt < maxRetries) continue;
      return { data: null, error };
    } finally {
      clearTimeout(timer);
    }
  }
  return { data: null, error: lastError || new Error("Failed after multiple attempts") };
}

// ── Skill normalization ──
const SKILL_ALIASES: Record<string, string> = {
  js: "JavaScript", ts: "TypeScript", py: "Python", "node.js": "Node.js",
  nodejs: "Node.js", "react.js": "React", reactjs: "React", k8s: "Kubernetes",
  gcp: "Google Cloud Platform", pg: "PostgreSQL", postgres: "PostgreSQL",
  mongo: "MongoDB", "ci/cd": "CI/CD", cicd: "CI/CD", golang: "Go",
  ml: "Machine Learning", dl: "Deep Learning", nlp: "Natural Language Processing",
};

function normalizeSkillName(skill: string): string {
  const trimmed = skill.trim();
  if (!trimmed) return "";
  const lower = trimmed.toLowerCase();
  return SKILL_ALIASES[lower] || trimmed;
}

function dedupeSkills(skills: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of skills) {
    const normalized = normalizeSkillName(raw);
    if (!normalized) continue;
    const key = normalized.toLowerCase();
    if (!seen.has(key)) { seen.add(key); result.push(normalized); }
  }
  return result;
}

const CATEGORY_LABELS: Record<string, string> = {
  programming_languages: "Programming Languages",
  frameworks: "Frameworks",
  libraries: "Libraries",
  databases: "Databases",
  cloud_platforms: "Cloud Platforms",
  devops: "DevOps",
  developer_tools: "Developer Tools",
  design_tools: "Design Tools",
  analytics_tools: "Analytics Tools",
  product_tools: "Product Tools",
  methodologies: "Methodologies",
  soft_skills: "Soft Skills",
  other: "Other Skills",
};

function categorySkillsToCoreStrengths(catSkills: any): SkillCategory[] {
  if (!catSkills || typeof catSkills !== "object") return [];
  const result: SkillCategory[] = [];
  for (const [key, label] of Object.entries(CATEGORY_LABELS)) {
    const skills = catSkills[key];
    if (!Array.isArray(skills) || skills.length === 0) continue;
    const deduped = dedupeSkills(skills);
    if (deduped.length === 0) continue;
    result.push({
      id: crypto.randomUUID(),
      category: label,
      skills: deduped.join(", "),
    });
  }
  return result;
}

function mergeAllSkillsFlat(aiData: any): string[] {
  const all: string[] = [];
  // From explicit skills array
  if (Array.isArray(aiData.skills)) all.push(...aiData.skills);
  // From extractedSkillsByCategory
  const catSkills = aiData.extractedSkillsByCategory || aiData.parsedResumeData?.extractedSkillsByCategory;
  if (catSkills && typeof catSkills === "object") {
    for (const arr of Object.values(catSkills)) {
      if (Array.isArray(arr)) all.push(...(arr as string[]));
    }
  }
  return dedupeSkills(all);
}

// ── Parse skill categories from raw resume text ──
// Handles three formats:
//   Format A: "Category: skill1, skill2" (colon-split on one line)
//   Format B: flat bullet list with no subheadings → single "Technical Skills" entry
//   Format C: short category header line followed by individual skill item lines
//             (some items may contain sub-label colons like "Ticketing Systems: Freshdesk, ServiceNow")
function parseSkillCategoriesFromRawText(rawText: string): SkillCategory[] {
  if (!rawText) return [];
  const lines = rawText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const results: SkillCategory[] = [];
  const flatSkillLines: string[] = [];

  const skillSectionRe = /^(skills|core skills|technical skills|core competencies|areas of expertise|proficiencies|qualifications|tools|technologies)/i;
  const nonSkillSectionRe = /^(experience|professional experience|work experience|education|summary|professional summary|objective|projects?|certifications?|awards?|additional|volunteer|publications?|languages?|interests?|hobbies?|organizations?|memberships?|affiliations?|leadership|activities)/i;
  // Lines that are clearly personal contact info — never treat as skills
  const personalInfoRe = /^(linkedin|github|portfolio|website|twitter|instagram|facebook|http|www\.)/i;

  let inSkillsSection = false;
  let currentCategory: string | null = null;
  let currentSkillItems: string[] = [];

  const stripBullet = (s: string) => s.replace(/^[\s•\-\*◦▪▸►\|]+/, "").trim();

  const flushCategory = () => {
    if (currentCategory && currentSkillItems.length > 0) {
      results.push({ id: crypto.randomUUID(), category: currentCategory, skills: currentSkillItems.join(", ") });
    }
    currentCategory = null;
    currentSkillItems = [];
  };

  // Decide if a line is a Format C category header.
  // strict=true (already inside a category): require ≤3 words so long skill lines aren't misidentified.
  // strict=false (no category yet): allow up to 5 words to catch "IT Support & Asset Management".
  const isCategoryHeader = (raw: string, strict: boolean): boolean => {
    if (/^[\s•\-\*◦▪▸►]/.test(raw)) return false;   // has bullet prefix → it's a skill
    const s = stripBullet(raw);
    if (s.length < 3 || s.length > 55) return false;
    if (personalInfoRe.test(s)) return false;
    if (/[()]/.test(s)) return false;                  // parentheticals → descriptive skill, not a header
    if (/\d/.test(s)) return false;                    // digits → "Tier 1", "365", etc. → skill
    // Colon with substantial content after = sub-labeled skill line, not a header
    const ci = s.indexOf(":");
    if (ci > 0 && s.slice(ci + 1).trim().length > 3) return false;
    const words = s.replace(/[^a-zA-Z\s&\/\-]/g, "").split(/\s+/).filter(Boolean);
    const maxWords = strict ? 3 : 5;
    if (words.length < 2 || words.length > maxWords) return false;
    return /^[A-Z]/.test(words[0]);
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const stripped = stripBullet(line);

    // ── Section boundary detection ──
    if (skillSectionRe.test(line) && (line.length < 45 || !line.includes(":"))) {
      inSkillsSection = true;
      continue;
    }
    if (nonSkillSectionRe.test(line) && line.length < 65) {
      flushCategory();
      inSkillsSection = false;
      continue;
    }

    if (!inSkillsSection) continue;
    if (personalInfoRe.test(stripped)) continue;   // skip LinkedIn, GitHub etc.

    // ── Format A: "Category: skill1, skill2" — colon-split, only inside skills section ──
    const colonIdx = stripped.indexOf(":");
    if (colonIdx > 0 && colonIdx < 55) {
      const left = stripped.slice(0, colonIdx).trim();
      const right = stripped.slice(colonIdx + 1).trim();

      // No current category + looks like a standalone Format A line
      if (!currentCategory && left.length >= 3 && left.length <= 50 && right.length >= 2 && !personalInfoRe.test(left)) {
        const parts = right.split(",").map(s => s.trim()).filter(Boolean);
        if (parts.length >= 1) {
          flushCategory();
          results.push({ id: crypto.randomUUID(), category: left, skills: parts.join(", ") });
          continue;
        }
      }

      // Inside a Format C category → this colon line is a sub-labeled skill item
      if (currentCategory) {
        currentSkillItems.push(stripped);
        continue;
      }
    }

    // ── Format C: category header detection ──
    if (isCategoryHeader(stripped, currentCategory !== null)) {
      flushCategory();
      currentCategory = stripped;
      continue;
    }

    // ── Two-line fallback: "Category Name" then next line is comma-separated skills ──
    if (!currentCategory && /^[A-Z]/.test(stripped) && !stripped.includes(",") && stripped.split(/\s+/).length <= 5) {
      const nextStripped = i + 1 < lines.length ? stripBullet(lines[i + 1]) : "";
      const nextParts = nextStripped.split(",").map(s => s.trim()).filter(Boolean);
      if (nextParts.length >= 2 && nextParts.every(s => s.length < 60)) {
        results.push({ id: crypto.randomUUID(), category: stripped, skills: nextParts.join(", ") });
        i++;
        continue;
      }
    }

    // ── Format C: skill item under current category ──
    if (currentCategory) {
      if (stripped.length > 0) currentSkillItems.push(stripped);
      continue;
    }

    // ── Format B fallback: flat bullet/pipe lines with no established category ──
    if (/^[\s•\-\*◦▪▸►]/.test(line) || (stripped.includes("|") && !stripped.includes(":"))) {
      const tokens = stripped.split(/[,|]/).map(s => s.trim()).filter(s => s.length > 0 && s.length < 80);
      flatSkillLines.push(...tokens);
    }
  }

  flushCategory(); // save last category

  // Format B fallback: no categories found → single "Technical Skills" entry
  if (results.length === 0 && flatSkillLines.length > 0) {
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const s of flatSkillLines) {
      const key = s.toLowerCase();
      if (!seen.has(key)) { seen.add(key); unique.push(s); }
    }
    results.push({ id: crypto.randomUUID(), category: "Technical Skills", skills: unique.join(", ") });
  }

  return results;
}

// ── Detect custom sections from raw text that AI may have missed ──
function detectMissedCustomSections(
  rawText: string,
  existingCustomTitles: string[],
  knownResumeEntries?: { companies: string[]; jobTitles: string[]; locations: string[]; degrees: string[]; institutions: string[]; personalNames?: string[] }
): { title: string; items: { title: string; bullets: string[] }[] }[] {
  if (!rawText) return [];
  const lines = rawText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const missed: { title: string; items: { title: string; bullets: string[] }[] }[] = [];
  
  // Standard sections that are NOT custom
  const standardSections = /^(skills|technical skills|core skills|core competencies|areas of expertise|experience|professional experience|work experience|education|summary|professional summary|objective|personal info|contact|header)/i;
  
  // Detect if we're inside a SKILLS section (to skip skill sub-headers)
  const skillSectionHeaders = /^(skills|technical skills|core skills|core competencies|areas of expertise|proficiencies|qualifications|tools|technologies)/i;
  const nonSkillSectionHeaders = /^(experience|professional experience|work experience|education|summary|objective|projects?|project experience|certifications?|awards?|additional|volunteer|publications?)/i;
  
  // Build a set of known resume content to filter false positives
  const knownEntriesLower = new Set<string>();
  if (knownResumeEntries) {
    for (const list of [knownResumeEntries.companies, knownResumeEntries.jobTitles, knownResumeEntries.locations, knownResumeEntries.degrees, knownResumeEntries.institutions, knownResumeEntries.personalNames || []]) {
      for (const entry of list) {
        if (entry) knownEntriesLower.add(entry.toLowerCase().trim());
      }
    }
  }

  const isLikelySectionHeader = (line: string): boolean => {
    if (line.length < 3 || line.length > 60) return false;
    // Filter out location patterns: "City, ST" or "City, State (Remote)"
    if (/^[A-Za-z\s]+,\s*[A-Z]{2}(\s*\(|$)/i.test(line)) return false;
    // Filter out date patterns
    if (/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december)\b.*\d{4}/i.test(line)) return false;
    // Filter out email/phone/URL patterns
    if (/@|\.com|\.org|\.net|\d{3}[-.\s]\d{3}[-.\s]\d{4}|https?:\/\/|linkedin/i.test(line)) return false;
    // Filter out lines that are known resume entries (companies, job titles, locations, degrees, institutions)
    if (knownEntriesLower.has(line.toLowerCase().trim())) return false;
    // Filter out lines containing pipes or bullet chars (contact info lines)
    if (/\|/.test(line)) return false;
    
    if (/^[A-Z][A-Z\s&,\/\-]+$/.test(line)) return true;
    if (/^[A-Z][a-zA-Z\s&,\/\-]+$/.test(line) && line.length < 45 && !line.includes(".") && line.split(" ").length <= 5) return true;
    return false;
  };
  
  // Check if the NEXT line after a header looks like comma-separated skills (skill sub-header indicator)
  const nextLineIsSkillsList = (idx: number): boolean => {
    if (idx + 1 >= lines.length) return false;
    const next = lines[idx + 1];
    const parts = next.split(",").map(s => s.trim()).filter(s => s.length > 0);
    return parts.length >= 2 && parts.every(s => s.length < 60);
  };
  
  const existingLower = new Set([
    ...existingCustomTitles.map(t => t.toLowerCase()),
    "skills", "technical skills", "core skills", "core competencies", "experience", "professional experience",
    "work experience", "education", "summary", "professional summary", "objective",
    "personal info", "contact", "header",
    // Common skill category names that should never become custom sections
    "programming languages", "frameworks", "libraries", "databases", "cloud platforms",
    "devops", "developer tools", "design tools", "analytics tools", "product tools",
    "methodologies", "soft skills", "other skills",
    "tools & platforms", "tools and platforms", "development & technical", "development and technical",
    "project & process", "project and process", "it support & asset management",
    "it support and asset management", "cloud & infrastructure", "languages & frameworks",
    "backend", "frontend", "full stack", "infrastructure", "security", "data & analytics",
    "data and analytics", "core competencies", "areas of expertise", "key skills",
  ]);
  
  let currentSection: { title: string; contentLines: string[] } | null = null;
  let inSkillsSection = false;
  // Track if we're inside experience or education sections to skip their content
  let inExperienceSection = false;
  let inEducationSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Track skills section to skip sub-headers within it
    if (isLikelySectionHeader(line) && skillSectionHeaders.test(line)) {
      if (currentSection && currentSection.contentLines.length > 0) {
        missed.push(buildCustomSection(currentSection));
      }
      currentSection = null;
      inSkillsSection = true;
      inExperienceSection = false;
      inEducationSection = false;
      continue;
    }
    
    // Track experience section
    if (/^(experience|professional experience|work experience)$/i.test(line)) {
      if (currentSection && currentSection.contentLines.length > 0) {
        missed.push(buildCustomSection(currentSection));
      }
      currentSection = null;
      inSkillsSection = false;
      inExperienceSection = true;
      inEducationSection = false;
      continue;
    }
    
    // Track education section
    if (/^education$/i.test(line)) {
      if (currentSection && currentSection.contentLines.length > 0) {
        missed.push(buildCustomSection(currentSection));
      }
      currentSection = null;
      inSkillsSection = false;
      inExperienceSection = false;
      inEducationSection = true;
      continue;
    }
    
    if (isLikelySectionHeader(line) && nonSkillSectionHeaders.test(line)) {
      inSkillsSection = false;
      inExperienceSection = false;
      inEducationSection = false;
    }
    
    // Skip everything inside experience and education sections
    if (inExperienceSection || inEducationSection) {
      if (isLikelySectionHeader(line) && nonSkillSectionHeaders.test(line) && !/^(experience|professional experience|work experience|education)$/i.test(line)) {
        // We've hit a new recognized section, exit experience/education
        inExperienceSection = false;
        inEducationSection = false;
        // Fall through to handle this as a potential section header
      } else if (isLikelySectionHeader(line) && !standardSections.test(line) && !existingLower.has(line.toLowerCase())) {
        // Check if this is a custom section header (like PROJECT EXPERIENCE, CERTIFICATIONS)
        const looksLikeCustomSection = /^(projects?|project experience|certifications?|awards?|additional|volunteer|publications?|leadership|activities|honors)/i.test(line);
        if (looksLikeCustomSection) {
          inExperienceSection = false;
          inEducationSection = false;
          // Fall through to handle as section header
        } else {
          continue; // Skip — likely a company name, job title, or location within experience/education
        }
      } else {
        continue; // Skip content lines within experience/education
      }
    }
    
    if (isLikelySectionHeader(line)) {
      // Skip ALL headers while inside skills section — skill category names are never custom sections
      if (inSkillsSection && !nonSkillSectionHeaders.test(line)) {
        if (nextLineIsSkillsList(i)) i++; // also skip the skills list line
        continue;
      }
      
      // Save previous section
      if (currentSection && currentSection.contentLines.length > 0) {
        missed.push(buildCustomSection(currentSection));
      }
      
      inSkillsSection = false; // we hit a non-skill header, exit skills section
      
      if (standardSections.test(line) || existingLower.has(line.toLowerCase())) {
        currentSection = null;
      } else {
        const title = line.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
        currentSection = { title, contentLines: [] };
      }
      continue;
    }
    
    // Collect content for current non-standard section
    if (currentSection && line.length > 3) {
      if (line.includes(":") && line.indexOf(":") < 40 && line.split(",").length >= 2) continue;
      if (/^\w+\s+\d{4}\s*[-–]\s*(present|\w+\s+\d{4})/i.test(line)) continue;
      if (/^[A-Z][a-z]+,\s*[A-Z]{2}(\s*\(|$)/.test(line)) continue;
      
      currentSection.contentLines.push(line.replace(/^[-•*◦▪▸►]\s*/, ""));
    }
  }
  
  // Don't forget last section
  if (currentSection && currentSection.contentLines.length > 0) {
    missed.push(buildCustomSection(currentSection));
  }
  
  return missed;
}

/** Convert detected section lines into properly structured custom section items */
function buildCustomSection(section: { title: string; contentLines: string[] }): { title: string; items: { type: 'item' | 'bullet'; title: string; bullets: string[] }[] } {
  const titleLower = section.title.toLowerCase();

  // Certifications, licenses, additional info, interests, hobbies: each line → simple bullet entry
  if (/certif|licens|additional|interests?|hobbies/i.test(titleLower)) {
    return {
      title: section.title,
      items: section.contentLines.map(line => ({
        type: 'bullet' as const,
        title: line,
        bullets: [],
      })),
    };
  }

  // Projects and others: structured item (first line = title, rest = bullets)
  return {
    title: section.title,
    items: [{
      type: 'item' as const,
      title: section.contentLines[0] || section.title,
      bullets: section.contentLines.length > 1 ? section.contentLines.slice(1) : [],
    }],
  };
}

// ── Transform AI response ──
function transformAIResumeData(aiData: any, rawResumeText?: string): ResumeData {
  if (!aiData) throw new Error("No resume data received from AI");

  const personalInfo = aiData.personalInfo || {};

  const experience: ExperienceItem[] = (aiData.experience || []).map((exp: any) => {
    const jobTitle = exp.title || exp.jobTitle || exp.role || "";
    const company = exp.company || exp.employer || exp.organization || "";
    const location = exp.city || exp.location || exp.cityState || exp.city_state || exp.region || "";
    const workType = normalizeWorkType(exp.workMode || exp.workType || exp.work_mode || exp.locationType || exp.workplace);

    const rawStart = exp.startDate || exp.start || exp.from || exp.start_date || exp.startMonth || "";
    const rawEnd = exp.endDate || exp.end || exp.to || exp.end_date || exp.endMonth || "";
    const rawRange = exp.dateRange || exp.dates || exp.employmentDates || exp.duration || "";

    const current = exp.current === true ||
      /^(present|current)$/i.test(String(rawEnd || rawRange || "")) ||
      String(exp.endDate ?? "").toLowerCase?.() === "present";

    let startDate = normalizeDateString(rawStart);
    let endDate = current ? "" : normalizeDateString(rawEnd);

    if (!startDate && (!endDate || current)) {
      const parsed = parseDateRange(rawRange);
      if (parsed) {
        startDate = startDate || parsed.startDate;
        if (!current) endDate = endDate || parsed.endDate;
      }
    }

    const bulletsRaw = Array.isArray(exp.bullets) ? exp.bullets
      : Array.isArray(exp.points) ? exp.points
      : Array.isArray(exp.achievements) ? exp.achievements : [];

    return {
      id: exp.id || crypto.randomUUID(),
      jobTitle, company, location, workType, startDate, endDate, current,
      bullets: Array.isArray(bulletsRaw) ? bulletsRaw.filter((b: any) => typeof b === "string" && b.trim()) : [],
    };
  });

  const education: EducationItem[] = (aiData.education || []).map((edu: any) => {
    const graduationRaw = edu.graduationDate || edu.gradDate || edu.endDate || edu.date || edu.year || edu.graduated || "";
    const gradNormalized = normalizeDateString(graduationRaw);
    // Guard: AI sometimes hallucinates past dates (e.g. "1905-07") for "Expected YYYY" text.
    // If the normalized year is before 1970, try extracting a valid year from the raw string instead.
    const gradYear = parseInt(gradNormalized.slice(0, 4), 10);
    let graduationDate: string;
    if (gradNormalized && gradYear >= 1970) {
      graduationDate = gradNormalized;
    } else {
      const rawStr = String(graduationRaw || "");
      const futureYear = rawStr.match(/\b(20\d{2}|19[7-9]\d)\b/);
      graduationDate = futureYear ? futureYear[0] : (gradNormalized || rawStr);
    }
    return {
      id: edu.id || crypto.randomUUID(),
      degree: edu.degree || "",
      field: edu.field || edu.major || "",
      institution: edu.institution || edu.school || edu.university || "",
      location: edu.location || edu.city || edu.campus || edu.place || "",
      graduationDate,
      gpa: edu.gpa || "",
    };
  });

  // Build coreStrengths: PRIORITIZE explicit coreStrengths from AI (preserves original category names)
  // Fall back to extractedSkillsByCategory, then to flat skills as last resort
  let coreStrengths: SkillCategory[] = [];
  
  // First: use explicit coreStrengths from AI — these preserve original resume category names
  const explicitCS: SkillCategory[] = (aiData.coreStrengths || [])
    .filter((cs: any) => cs.category && cs.skills)
    .map((cs: any) => ({
      id: cs.id || crypto.randomUUID(),
      category: cs.category.trim(),
      skills: typeof cs.skills === "string" ? cs.skills.trim() 
        : Array.isArray(cs.skills) ? dedupeSkills(cs.skills).join(", ") : "",
    }))
    .filter((cs: SkillCategory) => cs.skills.length > 0);

  if (explicitCS.length > 0) {
    // Deduplicate categories by merging skills with same category name
    const catMap = new Map<string, SkillCategory>();
    for (const cs of explicitCS) {
      const key = cs.category.toLowerCase();
      if (catMap.has(key)) {
        const existing = catMap.get(key)!;
        const merged = dedupeSkills([...existing.skills.split(", "), ...cs.skills.split(", ")]);
        existing.skills = merged.join(", ");
      } else {
        catMap.set(key, { ...cs });
      }
    }
    coreStrengths = Array.from(catMap.values());
  }

  // Fallback 1: use extractedSkillsByCategory (predefined categories) if no explicit coreStrengths
  if (coreStrengths.length === 0) {
    const catSkills = aiData.extractedSkillsByCategory;
    if (catSkills && typeof catSkills === "object" && Object.keys(catSkills).length > 0) {
      coreStrengths = categorySkillsToCoreStrengths(catSkills);
    }
  }

  // Fallback 2: parse skill categories directly from raw resume text (colon-splitting)
  // Triggers when AI returned fewer categories than raw text has — ensures consistency across models
  if (rawResumeText) {
    const fromRawText = parseSkillCategoriesFromRawText(rawResumeText);
    if (fromRawText.length > 1 && fromRawText.length > coreStrengths.length) {
      // Raw text parsing found more categories — use them for better granularity
      coreStrengths = fromRawText;
    }
  }

  // Fallback 3: if STILL empty but we have flat skills, group them into a single "Technical Skills" category
  if (coreStrengths.length === 0) {
    const flatSkills = mergeAllSkillsFlat(aiData);
    if (flatSkills.length > 0) {
      coreStrengths = [{
        id: crypto.randomUUID(),
        category: "Technical Skills",
        skills: flatSkills.join(", "),
      }];
    }
  }

  // Build flat skills list from all sources
  const allSkills = mergeAllSkillsFlat(aiData);

  const rawCustomSections = aiData.customSections || aiData.custom_sections || aiData.additionalSections ||
    aiData.otherSections || aiData.extraSections || aiData.sections || [];

  const customSections: CustomSection[] = (rawCustomSections || [])
    .map((section: any) => ({
      id: section.id || crypto.randomUUID(),
      title: section.title || section.name || section.heading || "",
      items: (section.items || [])
        .map((item: any) => {
          const title = item.name || item.title || "";
          const subtitle = item.subtitle || "";
          const date = item.date || "";
          const description = item.description || item.details || item.summary || "";
          const link = item.link || "";
          const bullets = Array.isArray(item.bullets) ? item.bullets.filter((b: any) => typeof b === "string" && b.trim())
            : Array.isArray(item.points) ? item.points.filter((b: any) => typeof b === "string" && b.trim()) : [];
          const hasStructure = subtitle || date || description || link || bullets.length > 0;
          return {
            id: item.id || crypto.randomUUID(),
            type: (hasStructure ? 'item' : 'bullet') as 'item' | 'bullet',
            title,
            subtitle,
            date,
            description,
            link,
            bullets,
          };
        })
        .filter((item: any) => {
          const hasTitle = item.title && item.title.trim() && !item.title.includes("____") && item.title !== "N/A";
          const hasContent = item.subtitle || item.description || item.link || (item.bullets && item.bullets.length > 0);
          return hasTitle || hasContent;
        }),
    }))
    .filter((section: any) => section.items && section.items.length > 0);

  // Detect custom sections from raw text that the AI may have missed
  // BUT be very conservative to avoid duplicating content already parsed
  if (rawResumeText) {
    // Collect ALL known titles: section titles + item titles within sections + skill category names
    const existingTitles = [
      ...customSections.map(s => s.title),
      ...customSections.flatMap(s => s.items.map(i => i.title)),
      ...coreStrengths.map(cs => cs.category),
    ];
    const knownResumeEntries = {
      companies: experience.map(e => e.company),
      jobTitles: experience.map(e => e.jobTitle),
      locations: [...experience.map(e => e.location), ...education.map(e => e.location)],
      degrees: education.map(e => e.degree),
      institutions: education.map(e => e.institution),
      personalNames: [
        personalInfo.fullName || "",
        personalInfo.email || "",
        personalInfo.phone || "",
        personalInfo.location || "",
      ].filter(Boolean),
    };
    const missed = detectMissedCustomSections(rawResumeText, existingTitles, knownResumeEntries);
    
    // Only add sections that don't overlap with existing content
    for (const m of missed) {
      // Skip if a custom section with similar title already exists
      const titleLower = m.title.toLowerCase();
      const alreadyExists = customSections.some(cs => {
        const csLower = cs.title.toLowerCase();
        return csLower === titleLower || csLower.includes(titleLower) || titleLower.includes(csLower);
      });
      if (alreadyExists) continue;
      
      // Skip if this looks like a skill category that's already in coreStrengths
      const isSkillDupe = coreStrengths.some(cs => {
        const csLower = cs.category.toLowerCase();
        return csLower === titleLower || csLower.includes(titleLower) || titleLower.includes(csLower);
      });
      if (isSkillDupe) continue;

      customSections.push({
        id: crypto.randomUUID(),
        title: m.title,
        items: m.items.map(item => ({
          id: crypto.randomUUID(),
          type: (item.type || (item.bullets.length > 0 ? 'item' : 'bullet')) as 'item' | 'bullet',
          title: item.title,
          subtitle: "",
          date: "",
          description: "",
          link: "",
          bullets: item.bullets,
        })),
      });
    }
  }

  return {
    personalInfo: {
      fullName: personalInfo.fullName || "",
      email: personalInfo.email || "",
      phone: personalInfo.phone || "",
      location: personalInfo.city || personalInfo.location || "",
      linkedin: personalInfo.linkedIn || personalInfo.linkedin || "",
      github: personalInfo.github || "",
      portfolio: personalInfo.portfolio || "",
    },
    summary: aiData.summary || "",
    coreStrengths, experience, education, customSections,
    skills: allSkills,
  };
}

// ── Result interfaces ──
interface OptimizationResult {
  optimizedSummary: string;
  optimizedCoreStrengths?: { id: string; category: string; skills: string }[];
  optimizedSkills?: string[];
  optimizedExperience: { id: string; optimizedBullets: string[] }[];
  atsScore: ATSScore;
  extractedKeywords: string[];
}

interface ImportAnalysisResult {
  parsedResumeData: ResumeData;
  analysis: ResumeAnalysis;
  atsScore?: ATSScore;
  extractedKeywords?: string[];
}

function normalizeAnalysis(data: any, atsScore?: any): ResumeAnalysis {
  const raw = data?.analysis ?? data?.resumeAnalysis ?? data?.initialAnalysis ?? {};
  const score = typeof raw?.score === "number" ? raw.score
    : typeof atsScore?.overall === "number" ? atsScore.overall : 0;
  return {
    weaknesses: toStringArray(raw?.weaknesses ?? raw?.keyWeaknesses),
    improvements: toStringArray(raw?.improvements ?? raw?.suggestedImprovements),
    missingKeywords: toStringArray(raw?.missingKeywords ?? raw?.keywordGaps),
    score,
  };
}

// ── Friendly error messages — NEVER tell user to shorten resume ──
function friendlyError(err: any): string {
  const msg = err?.message || "";
  const lower = msg.toLowerCase();
  if (msg.includes("API key") || msg.includes("Payment")) return msg;
  if (msg.includes("Rate limit")) return "Too many requests. Please wait a moment and try again.";
  if (lower.includes("timed out") || lower.includes("abort") || lower.includes("longer than expected")) {
    return "Resume parsing is taking longer than expected. Please try again.";
  }
  if (lower.includes("network") || lower.includes("failed to fetch")) {
    return "Network issue while parsing resume. Please check your connection and retry.";
  }
  return "Something went wrong. Please try again.";
}

// ── Public API: Streaming-first with non-streaming fallback ──

export const optimizeResume = async (
  resumeData: ResumeData,
  jobDescription?: string,
  existingSuggestions?: string[],
  onProgress?: ProgressCallback
): Promise<OptimizationResult> => {
  const progress = createMonotonicProgress(onProgress);
  const cacheKey = generateCacheKey({ resumeData, jobDescription, existingSuggestions });
  const cached = getCached<OptimizationResult>(cacheKey);
  if (cached) {
    progress?.("Using cached result...", 100);
    return cached;
  }

  progress?.("Starting optimization...", 5);

  try {
    const data = await invokeStreamingFunction<OptimizationResult>(
      { action: "optimize", resumeData, jobDescription, existingSuggestions },
      progress,
    );
    progress?.("Done!", 100);
    setCache(cacheKey, data);
    return data;
  } catch (streamError) {
    console.warn("Streaming failed, falling back:", streamError);
    progress?.("Switching to alternate processing...", undefined);
    try {
      const { data, error } = await invokeFunctionWithRetry<OptimizationResult>("optimize-resume", {
        body: { resumeData, jobDescription, existingSuggestions },
      }, NON_STREAM_TIMEOUT_MS, MAX_RETRIES, progress);
      if (error) throw new Error(error.message || "Failed to optimize resume.");
      if (!data) throw new Error("No data returned from optimization.");
      progress?.("Done!", 100);
      setCache(cacheKey, data);
      return data;
    } catch (fallbackError) {
      throw new Error(friendlyError(fallbackError));
    }
  }
};

export const analyzeImportedResume = async (
  rawResumeText: string,
  onProgress?: ProgressCallback
): Promise<ImportAnalysisResult> => {
  const progress = createMonotonicProgress(onProgress);
  progress?.("Starting resume parsing...", 5);

  try {
    const data = await invokeStreamingFunction<any>(
      { action: "import", rawResumeText },
      progress,
    );
    progress?.("Building resume...", 95);
    return {
      parsedResumeData: transformAIResumeData(data.parsedResumeData, rawResumeText),
      analysis: normalizeAnalysis(data, data.atsScore),
      atsScore: data.atsScore,
      extractedKeywords: data.extractedKeywords,
    };
  } catch (streamError) {
    console.warn("Streaming failed, falling back:", streamError);
    progress?.("Switching to alternate processing...", undefined);
    try {
      const { data, error } = await invokeFunctionWithRetry<any>("optimize-resume", {
        body: { action: "import", rawResumeText },
      }, NON_STREAM_TIMEOUT_MS, MAX_RETRIES, progress);
      if (error) throw new Error(error.message || "Failed to analyze resume.");
      if (!data?.parsedResumeData) throw new Error("Analysis returned incomplete data.");
      progress?.("Building resume...", 95);
      return {
        parsedResumeData: transformAIResumeData(data.parsedResumeData, rawResumeText),
        analysis: normalizeAnalysis(data, data.atsScore),
        atsScore: data.atsScore,
        extractedKeywords: data.extractedKeywords,
      };
    } catch (fallbackError) {
      throw new Error(friendlyError(fallbackError));
    }
  }
};

export const tailorResumeToJob = async (
  rawResumeText: string,
  jobDescription: string,
  onProgress?: ProgressCallback
): Promise<ImportAnalysisResult> => {
  const progress = createMonotonicProgress(onProgress);
  progress?.("Analyzing job requirements...", 5);

  try {
    const data = await invokeStreamingFunction<any>(
      { action: "tailor", rawResumeText, jobDescription },
      progress,
      TAILOR_STREAM_TIMEOUT_MS,
    );
    progress?.("Finalizing tailored resume...", 95);
    return {
      parsedResumeData: transformAIResumeData(data.parsedResumeData, rawResumeText),
      analysis: normalizeAnalysis(data, data.atsScore),
      atsScore: data.atsScore,
      extractedKeywords: data.extractedKeywords,
    };
  } catch (streamError) {
    console.warn("Streaming failed, falling back:", streamError);
    progress?.("Switching to alternate processing...", undefined);
    try {
      const { data, error } = await invokeFunctionWithRetry<any>("optimize-resume", {
        body: { action: "tailor", rawResumeText, jobDescription },
      }, TAILOR_NON_STREAM_TIMEOUT_MS, MAX_RETRIES, progress);
      if (error) throw new Error(error.message || "Failed to tailor resume.");
      if (!data?.parsedResumeData) throw new Error("Tailoring returned incomplete data.");
      progress?.("Finalizing...", 95);
      return {
        parsedResumeData: transformAIResumeData(data.parsedResumeData, rawResumeText),
        analysis: normalizeAnalysis(data, data.atsScore),
        atsScore: data.atsScore,
        extractedKeywords: data.extractedKeywords,
      };
    } catch (fallbackError) {
      throw new Error(friendlyError(fallbackError));
    }
  }
};

export const compressResumeToOnePage = async (
  resumeData: ResumeData,
  onProgress?: ProgressCallback
): Promise<ResumeData> => {
  onProgress?.("Compressing resume...", 10);
  try {
    const { data, error } = await invokeFunctionWithRetry<any>("optimize-resume", {
      body: { action: "compress", resumeData },
    }, NON_STREAM_TIMEOUT_MS, MAX_RETRIES, onProgress);
    if (error) throw new Error(error.message || "Failed to compress resume.");
    if (!data?.compressedResumeData) throw new Error("No data returned from compression.");
    onProgress?.("Done!", 100);
    return transformAIResumeData(data.compressedResumeData);
  } catch (err) {
    throw new Error(friendlyError(err));
  }
};

export const applyOptimizations = (currentData: ResumeData, optimizations: OptimizationResult): ResumeData => {
  const updatedExperience = currentData.experience.map((exp) => {
    const optimizedExp = optimizations.optimizedExperience.find((o) => o.id === exp.id);
    if (optimizedExp && optimizedExp.optimizedBullets) return { ...exp, bullets: optimizedExp.optimizedBullets };
    return exp;
  });

  let updatedCoreStrengths = currentData.coreStrengths || [];
  if (optimizations.optimizedCoreStrengths && optimizations.optimizedCoreStrengths.length > 0) {
    updatedCoreStrengths = optimizations.optimizedCoreStrengths.map((cs) => ({
      id: cs.id || crypto.randomUUID(), category: cs.category, skills: cs.skills,
    }));
  }
  // Merge extractedSkillsByCategory into coreStrengths if available
  const catSkills = (optimizations as any).extractedSkillsByCategory;
  if (catSkills && typeof catSkills === "object") {
    const fromCat = categorySkillsToCoreStrengths(catSkills);
    if (fromCat.length > 0 && updatedCoreStrengths.length === 0) {
      updatedCoreStrengths = fromCat;
    } else if (fromCat.length > 0) {
      const existingCats = new Set(updatedCoreStrengths.map(c => c.category.toLowerCase()));
      for (const cs of fromCat) {
        if (!existingCats.has(cs.category.toLowerCase())) updatedCoreStrengths.push(cs);
      }
    }
  }

  let updatedSkills = currentData.skills;
  if (optimizations.optimizedSkills) updatedSkills = dedupeSkills(optimizations.optimizedSkills);

  return {
    ...currentData,
    summary: optimizations.optimizedSummary || currentData.summary,
    coreStrengths: updatedCoreStrengths,
    skills: updatedSkills,
    experience: updatedExperience,
  };
};
